import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../components/page-header';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus } from 'lucide-react';
import { VacationPolicy } from '../types';
import { CreateVacationPolicyDto, UpdateVacationBalanceDto } from '../services';
import {
  PolicyList,
  PolicyFormDialog,
  MemberBalanceOverride,
} from '../components/vacations';
import {
  useVacationPolicies,
  useCreateVacationPolicy,
  useUpdateVacationPolicy,
  useDeleteVacationPolicy,
  useVacationBalance,
  useUpdateMemberBalance,
  useUsers,
  useIsResourceAdmin,
} from '../hooks';

const UUID_REGEX = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

const VacationAdmin: React.FC = () => {
  const { t } = useTranslation('vacations');
  const navigate = useNavigate();
  const { isAdmin, isLoading: adminLoading } = useIsResourceAdmin();

  // ---- Policies ----
  const { data: policies = [], isLoading: policiesLoading } = useVacationPolicies(isAdmin);
  const createPolicy = useCreateVacationPolicy();
  const updatePolicy = useUpdateVacationPolicy();
  const deletePolicy = useDeleteVacationPolicy();
  const [policyDialogOpen, setPolicyDialogOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<VacationPolicy | null>(null);
  const [policyError, setPolicyError] = useState<string | null>(null);

  // ---- Balance override ----
  const { data: users = [] } = useUsers(false);
  const currentYear = new Date().getFullYear();
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [year, setYear] = useState(currentYear);
  const { data: balance, isLoading: balanceLoading } = useVacationBalance(
    selectedMemberId,
    year,
    isAdmin && !!selectedMemberId,
  );
  const updateBalance = useUpdateMemberBalance();
  const [balanceError, setBalanceError] = useState<string | null>(null);
  const [balanceSuccess, setBalanceSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (!adminLoading && !isAdmin) {
      navigate('/vacations', { replace: true });
    }
  }, [adminLoading, isAdmin, navigate]);

  if (adminLoading || !isAdmin) {
    return null;
  }

  const handleCreatePolicyClick = () => {
    setEditingPolicy(null);
    setPolicyError(null);
    setPolicyDialogOpen(true);
  };

  const handleEditPolicy = (policy: VacationPolicy) => {
    setEditingPolicy(policy);
    setPolicyError(null);
    setPolicyDialogOpen(true);
  };

  const handleDeletePolicy = async (policy: VacationPolicy) => {
    if (!window.confirm(t('admin.policies.deleteConfirm', { name: policy.name }))) return;
    try {
      await deletePolicy.mutateAsync(policy.id);
    } catch (error) {
      console.error('Error deleting policy:', error);
    }
  };

  const handlePolicySubmit = async (data: CreateVacationPolicyDto) => {
    setPolicyError(null);
    try {
      if (editingPolicy) {
        await updatePolicy.mutateAsync({ id: editingPolicy.id, data });
      } else {
        await createPolicy.mutateAsync(data);
      }
      setPolicyDialogOpen(false);
      setEditingPolicy(null);
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        t('admin.policies.error.save');
      setPolicyError(message || null);
    }
  };

  // Backend balance errors can interpolate internal UUIDs (member/org ids) into
  // the message. Never surface those to users: fall back to a localized message
  // referencing the member by name, and only pass through backend messages that
  // are UUID-free (e.g. the FLEX allowance validation error).
  const getFriendlyBalanceError = (err: unknown): string => {
    const backendMessage = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
    const memberName = users.find((u) => u.id === selectedMemberId)?.name;
    if (backendMessage && !UUID_REGEX.test(backendMessage)) {
      return backendMessage;
    }
    if (memberName) {
      return t('admin.balance.errorForMember', { name: memberName });
    }
    return t('admin.balance.error');
  };

  const handleBalanceSubmit = async (data: UpdateVacationBalanceDto) => {
    setBalanceError(null);
    setBalanceSuccess(null);
    try {
      await updateBalance.mutateAsync({ memberId: selectedMemberId, year, data });
      setBalanceSuccess(t('admin.balance.success'));
    } catch (err) {
      setBalanceError(getFriendlyBalanceError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="vacation-admin-page">
      <PageHeader title={t('admin.title')} description={t('admin.description')} testId="vacation-admin-header" />

      <Tabs defaultValue="policies">
        <TabsList>
          <TabsTrigger value="policies" data-testid="vacation-admin-tab-policies">
            {t('admin.tabs.policies')}
          </TabsTrigger>
          <TabsTrigger value="balances" data-testid="vacation-admin-tab-balances">
            {t('admin.tabs.balances')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="policies" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={handleCreatePolicyClick} data-testid="vacation-admin-add-policy">
              <Plus className="mr-2 h-4 w-4" />
              {t('admin.policies.addPolicy')}
            </Button>
          </div>
          <PolicyList
            policies={policies}
            loading={policiesLoading}
            onEdit={handleEditPolicy}
            onDelete={handleDeletePolicy}
          />
        </TabsContent>

        <TabsContent value="balances">
          <Card>
            <CardContent className="pt-6">
              <MemberBalanceOverride
                members={users}
                selectedMemberId={selectedMemberId}
                year={year}
                balance={balance}
                balanceLoading={balanceLoading}
                isSubmitting={updateBalance.isPending}
                serverError={balanceError}
                successMessage={balanceSuccess}
                onMemberChange={(id) => {
                  setSelectedMemberId(id);
                  setBalanceError(null);
                  setBalanceSuccess(null);
                }}
                onYearChange={(y) => {
                  setYear(y);
                  setBalanceError(null);
                  setBalanceSuccess(null);
                }}
                onSubmit={handleBalanceSubmit}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <PolicyFormDialog
        policy={editingPolicy}
        open={policyDialogOpen}
        isSubmitting={createPolicy.isPending || updatePolicy.isPending}
        serverError={policyError}
        onOpenChange={(open) => {
          setPolicyDialogOpen(open);
          if (!open) setEditingPolicy(null);
        }}
        onSubmit={handlePolicySubmit}
      />
    </div>
  );
};

export default VacationAdmin;
